<template>
  <!-- T00MUCZVT2UP — store badges: logo, a small line, the store's name. -->
  <div
    v-if="variant === 'badge'"
    class="cc-app-store-links"
  >
    <a
      v-for="store in stores"
      :key="store.key"
      class="cc-app-store-badge"
      :href="store.href"
      target="_blank"
      rel="noopener"
      :aria-label="`${t(`appPromo.badge.${store.key}.line`)} ${t(`appPromo.badge.${store.key}.name`)} (${t('appPromo.badge.newTab')})`"
    >
      <v-icon
        class="cc-app-store-badge__icon"
        :icon="store.icon"
        size="32"
        aria-hidden="true"
      />
      <span class="cc-app-store-badge__text">
        <span class="cc-app-store-badge__line">{{ t(`appPromo.badge.${store.key}.line`) }}</span>
        <span class="cc-app-store-badge__name">{{ t(`appPromo.badge.${store.key}.name`) }}</span>
      </span>
    </a>
  </div>
  <div
    v-else
    class="cc-app-store-links"
  >
    <v-btn
      v-for="store in stores"
      :key="store.key"
      class="cc-app-store-links__button"
      variant="outlined"
      :color="color"
      :size="size"
      :href="store.href"
      target="_blank"
      rel="noopener"
      :prepend-icon="store.icon"
      :append-icon="Icons.OPEN_IN_NEW"
    >
      {{ t(`appPromo.stores.${store.key}`) }}
    </v-btn>
  </div>
</template>

<script setup lang="ts">
//================= [CSS] =====================================

//================= [JS] ======================================
import {AppStores} from '@/common/enums/AppStores';
import {Icons} from '@simpleforyou/client-core';
//================= [VUE COMPONENTS] ==========================
import {useI18n} from 'vue-i18n';

// Both places that offer the app use this: the one-off dialog (buttons) and
// the footer (T00MUCZVT2UP — store badges). They differ only in how they look,
// which is what the props carry.
withDefaults(defineProps<{size?: string, color?: string, variant?: 'buttons' | 'badge'}>(), {
  size: 'large',
  color: 'primary',
  variant: 'buttons',
});

const {t} = useI18n();

const stores = [
  {key: 'ios', href: AppStores.IOS, icon: Icons.APPLE},
  {key: 'android', href: AppStores.ANDROID, icon: Icons.GOOGLE_PLAY},
] as const;
</script>

<style scoped lang="scss">
.cc-app-store-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}

.cc-app-store-links__button {
  flex: 0 1 auto;
}

// Text and frame in the footer colour of the tenant, on a light veil of it:
// fits any corporate design without its own brand colours.
.cc-app-store-badge {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-width: 200px;
  padding: 10px 18px;
  border: 1px solid color-mix(in srgb, currentColor 35%, transparent);
  border-radius: 10px;
  background: color-mix(in srgb, currentColor 10%, transparent);
  color: var(--primary-fc, #fff);
  text-align: left;
  text-decoration: none;
  transition: background-color 0.15s ease;

  &:hover {
    background: color-mix(in srgb, currentColor 18%, transparent);
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
}

.cc-app-store-badge__text {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}

.cc-app-store-badge__line {
  font-size: 0.75rem;
  opacity: 0.85;
}

.cc-app-store-badge__name {
  font-size: 1.35rem;
  font-weight: 500;
}
</style>
